/**
 * Runtime configuration loader
 * Fetches /config.json at runtime to support build-once, deploy-many architecture
 */

export interface AppConfig {
  apiUrl: string
  googleMapsApiKey?: string
  environment: string
  sentryDsn?: string
}

// Default config for local development (fallback when /config.json doesn't exist)
const DEFAULT_CONFIG: AppConfig = {
  apiUrl: 'http://localhost:8000',
  environment: 'development',
}

// Singleton config instance
let config: AppConfig | null = null
let configLoadPromise: Promise<AppConfig> | null = null

/**
 * Load configuration from /config.json
 * Handles local dev fallback if config file doesn't exist
 */
export async function loadConfig(): Promise<AppConfig> {
  // Return cached config if already loaded
  if (config) {
    return config
  }

  // Return existing promise if load is in progress
  if (configLoadPromise) {
    return configLoadPromise
  }

  // Start loading config
  configLoadPromise = (async () => {
    try {
      const response = await fetch('/config.json', {
        cache: 'no-store', // Always fetch fresh config
      })

      if (!response.ok) {
        // If config.json doesn't exist (404), use defaults for local dev
        if (response.status === 404) {
          console.warn('⚠️ /config.json not found, using default local development config')
          config = DEFAULT_CONFIG
          return config
        }

        throw new Error(`Failed to load config: ${response.status} ${response.statusText}`)
      }

      const loadedConfig = await response.json()

      // Validate required fields
      if (!loadedConfig.apiUrl) {
        throw new Error('Config missing required field: apiUrl')
      }
      if (!loadedConfig.environment) {
        throw new Error('Config missing required field: environment')
      }

      config = {
        apiUrl: loadedConfig.apiUrl,
        googleMapsApiKey: loadedConfig.googleMapsApiKey,
        environment: loadedConfig.environment,
        sentryDsn: loadedConfig.sentryDsn,
      }

      console.log('Config loaded:', {
        apiUrl: config.apiUrl,
        environment: config.environment,
        hasGoogleMapsKey: !!config.googleMapsApiKey,
        hasSentryDsn: !!config.sentryDsn,
      })

      return config
    } catch (error) {
      // Network error or JSON parse error - use defaults for local dev
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn('⚠️ Failed to fetch /config.json, using default local development config')
        config = DEFAULT_CONFIG
        return config
      }

      // Re-throw other errors (validation errors, etc.)
      console.error('❌ Error loading config:', error)
      throw error
    } finally {
      // Clear the promise so we can retry if needed
      configLoadPromise = null
    }
  })()

  return configLoadPromise
}

/**
 * Get the current config (must be loaded first)
 * Throws if config hasn't been loaded yet
 */
export function getConfig(): AppConfig {
  if (!config) {
    throw new Error('Config not loaded. Call loadConfig() first.')
  }
  return config
}

/**
 * Reset config (useful for testing)
 */
export function resetConfig(): void {
  config = null
  configLoadPromise = null
}

